import { Select, SelectProps, Spin } from 'antd'
import { PtoItemType, ResponsePagingType } from 'Modules'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { DEFAULT_PAGE_INDEX } from 'constants/pagination'
import { logError } from 'utils/logger'

interface SelectPtoProps extends SelectProps {
  filter?: (pto: PtoItemType) => boolean
  sortFn?: (a: PtoItemType, b: PtoItemType) => number
  pageSize?: number
  soldOut?: number
}

const SCROLL_THRESHOLD = 90
const LOADING_COOLDOWN = 300

const SelectPto = ({
  value,
  onChange,
  placeholder = 'All PTO',
  filter,
  sortFn,
  pageSize = 100,
  soldOut,
  ...rest
}: SelectPtoProps) => {
  const [items, setItems] = useState<PtoItemType[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [, setPage] = useState(DEFAULT_PAGE_INDEX)
  const isLoadingRef = useRef(false)

  const fetchPtos = useCallback(
    async (pageNum: number) => {
      if (isLoadingRef.current) return

      isLoadingRef.current = true
      setLoading(true)

      try {
        const res = await AppAdmin.getListPTOAll({
          page: pageNum,
          limit: pageSize,
          soldOut: soldOut,
        })

        if (res.data?.success) {
          const { results, totalPages } = (res.data.data || {}) as ResponsePagingType<PtoItemType>
          const next = results || []

          setItems((prev) => (pageNum === DEFAULT_PAGE_INDEX ? next : [...prev, ...next]))
          setHasMore(pageNum < totalPages)
        }
      } catch (error) {
        logError('Error fetching PTOs:', error)
        setHasMore(false)
      } finally {
        setLoading(false)
        setTimeout(() => {
          isLoadingRef.current = false
        }, LOADING_COOLDOWN)
      }
    },
    [pageSize, soldOut]
  )

  useEffect(() => {
    fetchPtos(DEFAULT_PAGE_INDEX)
  }, [fetchPtos])

  const options = useMemo(() => {
    let list = items
    if (filter) list = list.filter(filter)
    if (sortFn) list = [...list].sort(sortFn)
    return list.map((item) => ({ value: item._id, label: item.fullName }))
  }, [items, filter, sortFn])

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    const { scrollTop, scrollHeight, clientHeight } = target
    const scrollPercentage = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)

    if (scrollPercentage > SCROLL_THRESHOLD && hasMore && !isLoadingRef.current) {
      setPage((prev) => {
        const nextPage = prev + 1
        fetchPtos(nextPage)
        return nextPage
      })
    }
  }

  return (
    <Select
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      optionFilterProp="label"
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      onPopupScroll={handlePopupScroll}
      virtual
      listHeight={300}
      notFoundContent={loading && options.length === 0 ? <Spin size="small" /> : 'No data'}
      dropdownStyle={{ padding: 8 }}
      dropdownRender={(menu) => (
        <>
          {menu}
          {loading && options.length > 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Spin size="small" />
              <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>Loading more...</div>
            </div>
          )}
        </>
      )}
      options={options}
      {...rest}
    />
  )
}

export default SelectPto
